/**
 * Dev-only component that fetches and displays the last email sent by the API.
 * Shows the email link inline so developers don't have to dig through console logs.
 *
 * Only renders when import.meta.env.DEV is true (stripped in production builds).
 */
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { Mail } from "lucide-react";

const AUTH_API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

interface DevEmail {
  to: string;
  subject: string;
  text: string;
  html: string;
  sentAt: string;
}

function extractUrl(text: string): string | null {
  const match = text.match(/https?:\/\/[^\s]+/);
  return match ? match[0] : null;
}

export function DevEmailPreview({
  "data-testid": testId,
}: {
  /**
   * Caller-supplied (signup and forgot-password both render one). The derived
   * `-link` id is how a local end-to-end run picks the verification /
   * password-reset URL out of the page instead of tailing API logs.
   */
  "data-testid"?: string;
} = {}) {
  const [email, setEmail] = useState<DevEmail | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    // Poll briefly for the email to appear (API processes async)
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${AUTH_API_URL}/api/dev/last-email`);
        if (res.ok) {
          const data = await res.json();
          setEmail(data);
          clearInterval(interval);
        }
      } catch {
        // API not available — ignore
      }
      if (attempts >= 10) clearInterval(interval);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  if (!import.meta.env.DEV || !email) return null;

  const url = extractUrl(email.text);

  return (
    <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20" data-testid={testId}>
      <Mail className="h-4 w-4 text-amber-600" />
      <AlertDescription>
        <p className="font-medium text-amber-800 dark:text-amber-200 text-xs mb-1">
          Dev Mode — Email Preview
        </p>
        <p className="text-xs text-muted-foreground mb-2">
          To: {email.to} &middot; {email.subject}
        </p>
        {url ? (
          <a
            href={url}
            className="text-sm font-medium text-primary hover:underline break-all"
            data-testid={testId ? `${testId}-link` : undefined}
          >
            {url}
          </a>
        ) : (
          <pre className="text-xs whitespace-pre-wrap max-h-32 overflow-auto">{email.text}</pre>
        )}
      </AlertDescription>
    </Alert>
  );
}
